




























































































import { EditorBlockView } from '@getaccept/editor-view';
import type { MergeValues } from '@getaccept/editor-lib';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { DocumentPageType } from '@getaccept/lib-shared/src/enums/document-page-type';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import PageSkeletonLoader from '@getaccept/lib-shared/src/skeleton-loader/components/PageSkeletonLoader.vue';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import DocumentContentPage from '@getaccept/lib-shared/src/components/DocumentContentPage.vue';
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import ContentLinkPage from '@getaccept/lib-shared/src/components/ContentLinkPage.vue';
import VideoPage from '@getaccept/lib-shared/src/components/VideoPage.vue';
import type { PartiallySignedEvent } from '@getaccept/editor-lib/src/types/signed-field';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import type { PropType, Ref } from '@vue/composition-api';
import { defineComponent, computed, ref, watch, nextTick } from '@vue/composition-api';
import { useIntersectionObserver, useThrottleFn } from '@vueuse/core';
import { CreateContextualCommentButtonState } from '../contextual-commenting/types/create-contextual-comment-button-state';

export default defineComponent({
  components: {
    VideoPage,
    ContentLinkPage,
    DocumentContentPage,
    PageSkeletonLoader,
    EditorBlockView,
  },
  props: {
    authId: {
      type: String,
      default: '',
    },
    token: {
      type: Object as PropType<{ jwt: string; exp: number }>,
      default: undefined,
    },
    documentId: {
      type: String,
      default: '',
    },
    page: {
      type: Object as PropType<DocumentPage>,
      required: true,
    },
    pageWidth: {
      type: Number,
      default: undefined,
    },
    recipient: {
      type: Object as PropType<Recipient>,
      default: null,
    },
    allRecipients: {
      type: Array as PropType<Recipient[]>,
      default: () => [],
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      default: undefined,
    },
    mergeValues: {
      type: Object as PropType<MergeValues>,
      default: undefined,
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      default: undefined,
    },
    focusedRequiredId: {
      type: String,
      default: '',
    },
    hideFields: {
      type: Boolean,
    },
    isPreview: {
      type: Boolean,
    },
    isSigned: {
      type: Boolean,
    },
    requiredStepsLeft: {
      type: Boolean,
    },
    initials: {
      type: String,
      default: '',
    },
    verifiedInitialFieldIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    comments: {
      type: Array as PropType<Comment[]>,
      default: () => [],
    },
    selectedCommentId: {
      type: String,
      default: '',
    },
    showContextualComments: {
      type: Boolean,
    },
    contextualCommentingDisabled: {
      type: Boolean,
    },
    createContextualCommentButtonState: {
      type: String as PropType<CreateContextualCommentButtonState>,
      default: CreateContextualCommentButtonState.Enabled,
    },
    partiallySignedFields: {
      type: Array as PropType<PartiallySignedEvent[]>,
      default: () => [],
    },
    editorRecipientInput: {
      type: Array as PropType<EditorRecipientInput[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const contentBlockContainer = ref<HTMLDivElement>();
    const isVisible = ref(false);
    const pageTooltip = ref(false);
    const pageRef: Ref<HTMLElement> = ref(null);

    const showPageTooltip = () => {
      if (isVisible.value) {
        pageTooltip.value = true;
        setTimeout(() => {
          pageTooltip.value = false;
        }, 2000);
      }
    };

    watch(isVisible, showPageTooltip);

    const filterComments = (comments: Comment[], editorBlockId: string): Comment[] =>
      comments.filter(comment => comment.editorSelection?.editorBlockId === editorBlockId);

    const handleUpdateField = ({ fieldId, value }: { fieldId: string; value: string }) => {
      emit('updateField', { fieldId, value });
    };

    const handleClickLinkField = (id: string) => {
      emit('clickLinkField', id);
    };

    const handleClickInitialsField = (id: string) => {
      emit('clickInitialsField', id);
    };

    const handleClickSignField = () => {
      emit('clickSignField');
    };

    useIntersectionObserver(
      contentBlockContainer,
      useThrottleFn(([{ isIntersecting }]) => {
        handleVisibility(isIntersecting);
      }, 300),
      { threshold: 0.1 }
    );

    const handleVisibility = (visible: boolean) => {
      if (visible && shouldFetchThumbUrl.value) {
        emit('load-page-thumb-url', props.page.id);
      }
      isVisible.value = visible;
      emit('page-visibility-change', {
        visible,
        pageId: props.page.id,
        element: pageRef.value,
      });
    };

    const shouldFetchThumbUrl = computed(
      () =>
        props.page.pageType === DocumentPageType.DocumentContent &&
        props.page.id &&
        !props.page.thumbUrl
    );

    const handleTrackVideo = (payload: { id: string; viewDuration: number }) => {
      emit('trackVideo', payload);
    };

    const handleImageIsLoaded = () => {
      isImageLoaded.value = true;
      nextTick(() => emit('imageLoaded'));
    };

    const handleContentLinkClick = (id: string) => {
      emit('contentLinkClick', id);
    };

    const skeletonHeight = computed(() =>
      props.page ? (props.pageWidth / props.page.pageWidth) * props.page.pageHeight : 900
    );

    const editorBlockId = computed(() => props.page?.editorBlock?.id);

    const editorContent = computed(() => props.page?.editorBlock?.content);

    const evaluateImageLoaded = computed(() =>
      [DocumentPageType.ContentLink, DocumentPageType.Video, DocumentPageType.Editor].includes(
        props.page.pageType
      )
    );

    const isImageLoaded = ref(evaluateImageLoaded.value);

    return {
      DocumentPageType,
      filterComments,
      handleUpdateField,
      handleClickLinkField,
      handleClickInitialsField,
      handleClickSignField,
      handleVisibility,
      shouldFetchThumbUrl,
      isVisible,
      pageTooltip,
      showPageTooltip,
      handleTrackVideo,
      handleImageIsLoaded,
      handleContentLinkClick,
      skeletonHeight,
      editorBlockId,
      editorContent,
      evaluateImageLoaded,
      isImageLoaded,
      pageRef,
      contentBlockContainer,
    };
  },
});
